// vg-binding: P7.D-159
//
// apps/vendor/src/features/products/detail/ProductDetailHeader.tsx
//
// Phase 7 Amendment #3 Task 83 — header block of /products/:id detail page.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import { InactiveCountdownChip } from '../InactiveCountdownChip.js';
import {
  useCancelInactivate,
  useInactivateProduct,
  useReactivateProduct,
  useResubmitProduct,
  type ProductRow,
  type ProductState,
  type ProductsApiError,
} from '../api.js';

const STATE_PILL: Record<ProductState, string> = {
  pending: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200',
  live: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200',
  inactive_countdown: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-200',
  inactive: 'bg-pw-surface text-pw-text',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200',
};

interface Props {
  product: ProductRow;
}

const EDIT_BLOCKED_STATES = new Set<ProductState>(['inactive', 'inactive_countdown']);

export function ProductDetailHeader({ product }: Props): JSX.Element {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const inactivateM = useInactivateProduct();
  const cancelM = useCancelInactivate();
  const reactivateM = useReactivateProduct();
  const resubmitM = useResubmitProduct();

  const base = { product_id: product._id, version: product.version };
  function onError(err: unknown): void {
    const e = err as ProductsApiError;
    setError(e.userMessage ?? e.message ?? t('table.actionFailed'));
  }

  const editBlocked = EDIT_BLOCKED_STATES.has(product.state);
  const editBtnBase =
    'inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold transition-colors';
  const editBtnEnabled =
    'bg-pw-primary text-white hover:bg-pw-primary-strong';
  const editBtnDisabled =
    'bg-pw-surface text-pw-text-muted cursor-not-allowed';
  const actionBtn =
    'inline-flex h-9 items-center rounded-md border border-pw-border bg-pw-surface px-3 text-sm font-medium text-pw-text hover:bg-pw-bg disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <header
      data-testid="product-detail-header"
      className="flex flex-col gap-3 rounded-xl border border-pw-border bg-pw-bg p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-pw-text">{product.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs text-pw-text-muted">
            <span className="font-mono">{product.sku_prefix}</span>
            <span>·</span>
            <span>{product.product_type}</span>
            <span>·</span>
            <span className="font-mono">v{product.version}</span>
            <span
              className={cn(
                'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold',
                STATE_PILL[product.state],
              )}
            >
              {product.state}
            </span>
            {product.state === 'inactive_countdown' ? (
              <InactiveCountdownChip ends_at={product.inactive_countdown_ends_at} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* UAT #2111 — title trên <button disabled> không hiện tooltip ở
              một số browser (disabled chặn mouse events). Bọc span mang
              title để hover luôn có gợi ý lý do. */}
          <span
            title={editBlocked ? t('detail.editDisabledTooltip') : undefined}
            className="inline-flex"
          >
            <button
              type="button"
              data-testid="product-detail-edit-btn"
              disabled={editBlocked}
              onClick={() => navigate(`/products/${product._id}/edit`)}
              aria-disabled={editBlocked}
              className={cn(editBtnBase, editBlocked ? editBtnDisabled : editBtnEnabled)}
            >
              {t('detail.editButton')}
            </button>
          </span>
          {editBlocked ? (
            <span
              data-testid="product-detail-edit-blocked-hint"
              className="text-xs text-pw-text-muted"
            >
              {t('detail.editDisabledTooltip')}
            </span>
          ) : null}

          {product.state === 'live' ? (
            <button
              type="button"
              data-testid="product-detail-inactivate-btn"
              disabled={inactivateM.isPending}
              onClick={() => {
                setError(null);
                inactivateM.mutate(base, { onError });
              }}
              className={actionBtn}
            >
              {t('detail.actions.inactivate')}
            </button>
          ) : null}

          {product.state === 'inactive_countdown' ? (
            <button
              type="button"
              data-testid="product-detail-cancel-countdown-btn"
              disabled={cancelM.isPending}
              onClick={() => {
                setError(null);
                cancelM.mutate(base, { onError });
              }}
              className={actionBtn}
            >
              {t('detail.actions.cancelCountdown')}
            </button>
          ) : null}

          {product.state === 'inactive' ? (
            <button
              type="button"
              data-testid="product-detail-reactivate-btn"
              disabled={reactivateM.isPending}
              onClick={() => {
                setError(null);
                reactivateM.mutate(base, { onError });
              }}
              className={actionBtn}
            >
              {t('detail.actions.reactivate')}
            </button>
          ) : null}

          {product.state === 'rejected' ? (
            <button
              type="button"
              data-testid="product-detail-resubmit-btn"
              disabled={resubmitM.isPending}
              onClick={() => {
                setError(null);
                resubmitM.mutate(base, { onError });
              }}
              className={actionBtn}
            >
              {t('detail.actions.resubmit')}
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </p>
      ) : null}

      {product.state === 'rejected' && product.reject_reason ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200">
          <strong>{t('detail.rejectReason')}</strong> {product.reject_reason}
        </div>
      ) : null}
    </header>
  );
}

export default ProductDetailHeader;
